import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePickerInput } from '@/components/ui/date-picker-input';
import { Loader2 } from 'lucide-react';

// Official lost reason keys - same as DealDetail.jsx
const LOST_REASON_KEYS = [
  'alreadyClient',
  'fearInsecurity',
  'ghostedNoResponse',
  'indirectInquiry',
  'invalidLead',
  'needsApproval',
  'noBudget',
  'noUrgency',
  'notEligible',
  'notInterested',
  'seasonalTiming',
  'wentWithCompetitor',
  'wrongClick'
];

interface LostReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, lostDate: Date) => void;
  isLoading?: boolean;
}

export function LostReasonModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}: LostReasonModalProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [lostDate, setLostDate] = useState<Date>(new Date());

  // Build reasons with translations (same pattern as DealDetail.jsx)
  const lostReasons = LOST_REASON_KEYS.map(key => ({
    value: key,
    label: t(`lostReasons.${key}`)
  }));

  const handleConfirm = () => {
    if (!reason) return;
    onConfirm(reason, lostDate);
    // Reset on close
    setReason('');
    setLostDate(new Date());
  };

  const handleClose = () => {
    setReason('');
    setLostDate(new Date());
    onClose();
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={handleClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('deals.markAsLost')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('deals.selectReason')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <div className="py-4 space-y-4">
          <div>
            <Label htmlFor="lost-reason">{t('fields.reason')} *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger id="lost-reason" className="border-slate-300 mt-2">
                <SelectValue placeholder={t('deals.selectReason')} />
              </SelectTrigger>
              <SelectContent>
                {lostReasons.map(r => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{t('fields.closedDate')} *</Label>
            <div className="mt-2">
              <DatePickerInput
                value={lostDate}
                onChange={(date) => date && setLostDate(date)}
              />
            </div>
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleClose} disabled={isLoading}>
            {t('common.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={!reason || isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              t('common.confirm')
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default LostReasonModal;
